/**
 * Analytics helper - Tool-agnostic event tracking layer
 * 
 * Usage:
 * - trackEvent('event_name', { property: 'value' })
 * - trackPageView('/path', 'Page Title')
 * 
 * GA4 Integration:
 * 1. Add your GA4 Measurement ID to index.html gtag script
 * 2. The gtag provider is automatically used when gtag is available
 * 
 * Database Storage:
 * Events are also stored in Supabase for the analytics dashboard
 */

import { supabase } from '@/integrations/supabase/client';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export interface EventProperties {
  [key: string]: string | number | boolean | undefined;
}

// Analytics provider interface for integrations
interface AnalyticsProvider {
  track: (eventName: string, properties?: EventProperties) => void;
  page: (path: string, title: string) => void;
}

// Generate a session ID for grouping events
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Store event in Supabase database
const storeEventInDatabase = async (
  eventName: string,
  properties?: EventProperties
): Promise<void> => {
  try {
    const { error } = await supabase.from('analytics_events').insert({
      event_name: eventName,
      event_properties: properties || {},
      page_path: window.location.pathname,
      session_id: getSessionId(),
    });
    
    if (error) {
      console.warn('[Analytics] Failed to store event in database:', error.message);
    }
  } catch (err) {
    // Fail silently - don't break the app for analytics
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Database storage error:', err);
    }
  }
};

// Console provider - logs in development when no GA4 is configured
const consoleProvider: AnalyticsProvider = {
  track: (eventName, properties) => {
    if (import.meta.env.DEV) {
      console.log(`[Analytics] Event: ${eventName}`, properties || {});
    }
  },
  page: (path, title) => {
    if (import.meta.env.DEV) {
      console.log(`[Analytics] Page View: ${path}`, { title });
    }
  },
};

// GA4 provider - uses gtag when available
const ga4Provider: AnalyticsProvider = {
  track: (eventName, properties) => {
    if (window.gtag) {
      // Filter out undefined values for cleaner GA4 data
      const cleanProperties = properties
        ? Object.fromEntries(
            Object.entries(properties).filter(([, v]) => v !== undefined)
          )
        : {};
      window.gtag('event', eventName, cleanProperties);
    }
    // Also log in dev for debugging
    if (import.meta.env.DEV) {
      console.log(`[GA4] Event: ${eventName}`, properties || {});
    }
  },
  page: (path, title) => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title,
      });
    }
    if (import.meta.env.DEV) {
      console.log(`[GA4] Page View: ${path}`, { title });
    }
  },
};

// Determine which provider to use based on gtag availability
function getActiveProvider(): AnalyticsProvider {
  if (typeof window !== 'undefined' && window.gtag) {
    return ga4Provider;
  }
  return consoleProvider;
}

// Current provider - dynamically resolved
let provider: AnalyticsProvider | null = null;

function getProvider(): AnalyticsProvider {
  if (!provider) {
    provider = getActiveProvider();
  }
  return provider;
}

/**
 * Configure a custom analytics provider
 * Call this once at app startup to override the default provider
 */
export function configureAnalytics(newProvider: AnalyticsProvider): void {
  provider = newProvider;
}

/**
 * Initialize GA4 with a measurement ID
 * Call this if you need to dynamically initialize GA4
 */
export function initializeGA4(measurementId: string): void {
  if (typeof window === 'undefined') return;
  
  // Initialize dataLayer if not present
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We handle page views manually
  });
  
  // Reset provider to pick up gtag
  provider = ga4Provider;
  
  if (import.meta.env.DEV) {
    console.log(`[GA4] Initialized with measurement ID: ${measurementId}`);
  }
}

/**
 * Track a custom event
 * Wraps all calls to fail silently and not block execution
 * Also stores events in the database for the analytics dashboard
 */
export function trackEvent(eventName: string, properties?: EventProperties): void {
  try {
    const enrichedProperties = {
      ...properties,
      timestamp: Date.now(),
    };
    
    // Send to analytics provider (GA4/console)
    getProvider().track(eventName, enrichedProperties);
    
    // Store in database for dashboard (async, non-blocking)
    storeEventInDatabase(eventName, enrichedProperties);
  } catch (error) {
    // Fail silently - analytics should never break the app
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to track event:', error);
    }
  }
}

/**
 * Track a page view
 * Also stores in database for analytics dashboard
 */
export function trackPageView(path: string, title: string): void {
  try {
    getProvider().page(path, title);
    
    // Store page view in database
    storeEventInDatabase('page_view', {
      page_path: path,
      page_title: title,
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Analytics] Failed to track page view:', error);
    }
  }
}

// ============================================
// Specific event helpers for consistent tracking
// ============================================

/**
 * Track demo CTA clicks
 */
export function trackDemoClick(location: string): void {
  trackEvent('demo_cta_click', {
    page_path: window.location.pathname,
    cta_location: location,
  });
}

/**
 * Track trial CTA clicks
 */
export function trackDemoBookingSubmit(details: {
  location: string;
  bookingType: 'live_demo_registration' | 'clarity_call';
  destinationUrl: string;
}): void {
  trackEvent('demo_booking_submit', {
    page_path: window.location.pathname,
    cta_location: details.location,
    booking_type: details.bookingType,
    destination_url: details.destinationUrl,
    lead_intent: 'book_demo',
    source: 'complyhub_website',
  });
}

/**
 * Track trial CTA clicks
 */
export function trackTrialClick(location: string, destinationUrl?: string): void {
  trackEvent('trial_cta_click', {
    page_path: window.location.pathname,
    cta_location: location,
    destination_url: destinationUrl || '/trial-signup',
  });
}

/**
 * Track a trial request confirmed as saved server-side.
 * Fires at most once per stored request id (deduped via sessionStorage)
 * so refreshes, retries and repeated clicks cannot double-count.
 * This is NOT trial activation — activation happens in the separate
 * onboarding application and is not signalled back to this site.
 */
export function trackTrialRequestSaved(requestId: string): void {
  try {
    const key = `trial_request_saved_${requestId}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    trackEvent('trial_request_saved', {
      page_path: window.location.pathname,
      request_id: requestId,
      lead_intent: 'start_trial',
      source: 'complyhub_website',
    });
    // Google Ads "Trial signup" conversion — fires only after confirmed storage
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    w.gtag?.('event', 'conversion', {
      send_to: 'AW-18336379055/CmJHCND1y-scEK_hu6dE',
      transaction_id: requestId,
    });
  } catch {
    // sessionStorage unavailable — skip rather than risk duplicate events
  }
}

/**
 * Track pricing page interactions
 */
export function trackPricingInteraction(
  interactionType: 'plan_chooser' | 'compare_toggle' | 'plan_select',
  details?: {
    users?: string;
    documents?: string;
    storage?: string;
    recommendedPlan?: string;
    toggleState?: boolean;
    plan?: string;
  }
): void {
  trackEvent('pricing_interaction', {
    page_path: window.location.pathname,
    interaction_type: interactionType,
    value_entered: details?.users || details?.documents || details?.storage
      ? `users:${details.users || '0'}/docs:${details.documents || '0'}/storage:${details.storage || '0'}`
      : undefined,
    recommended_plan: details?.recommendedPlan,
    toggle_state: details?.toggleState,
  });
}

/**
 * Track feature section views (for scroll-based tracking)
 */
export function trackFeatureSectionView(featureName: string): void {
  trackEvent('feature_section_view', {
    page_path: window.location.pathname,
    feature_name: featureName,
  });
}

/**
 * Track scroll depth milestones
 */
export function trackScrollDepth(pageName: string, depthPercentage: number): void {
  trackEvent('scroll_depth', {
    page_path: window.location.pathname,
    page_name: pageName,
    depth_percentage: depthPercentage,
    depth_threshold: `${depthPercentage}%`,
  });
}

// ============================================
// Conversion Optimization Tracking
// ============================================

/**
 * Track time spent on page at specific thresholds
 */
export function trackTimeOnPage(pageName: string, seconds: number): void {
  trackEvent('time_on_page', {
    page_path: window.location.pathname,
    page_name: pageName,
    time_seconds: seconds,
    time_threshold: `${seconds}s`,
  });
}

/**
 * Track video watch progress
 */
export function trackVideoProgress(videoId: string, percentWatched: number): void {
  trackEvent('video_progress', {
    page_path: window.location.pathname,
    video_id: videoId,
    percent_watched: percentWatched,
    progress_threshold: `${percentWatched}%`,
  });
}

/**
 * Track FAQ/accordion expansion
 */
export function trackFAQExpand(questionId: string, pagePath?: string): void {
  trackEvent('faq_expand', {
    page_path: pagePath || window.location.pathname,
    question_id: questionId,
  });
}

/**
 * Track form interaction start (first field focus)
 */
export function trackFormStart(formName: string): void {
  const sessionKey = `form_started_${formName}`;
  if (sessionStorage.getItem(sessionKey)) return;
  
  sessionStorage.setItem(sessionKey, 'true');
  trackEvent('form_start', {
    page_path: window.location.pathname,
    form_name: formName,
  });
}

/**
 * Track form completion
 */
export function trackFormComplete(formName: string): void {
  trackEvent('form_complete', {
    page_path: window.location.pathname,
    form_name: formName,
  });
}

/**
 * Track newsletter/email subscription
 */
export function trackSubscribe(location: string, email?: string): void {
  trackEvent('email_subscribe', {
    page_path: window.location.pathname,
    subscribe_location: location,
    // Don't store email in analytics for privacy
  });
}

// ============================================
// Contact page — demo booking funnel
// ============================================

/**
 * Track a click on a "Book a demo" CTA (hero buttons, phone, in-page anchors).
 */
export function trackBookDemoCtaClick(details: {
  location: string;
  ctaLabel: string;
  destination?: string;
}): void {
  trackEvent('book_demo_cta_click', {
    page_path: window.location.pathname,
    cta_location: details.location,
    cta_label: details.ctaLabel,
    destination_url: details.destination,
    lead_intent: 'book_demo',
    source: 'complyhub_website',
  });

  // Google Ads / GA4 conversion-friendly duplicate
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'generate_lead', {
      page_path: window.location.pathname,
      cta_location: details.location,
      lead_intent: 'book_demo',
      source: 'complyhub_website',
    });
  }
}

/**
 * Track a completed OnceHub booking on /contact.
 */
export function trackDemoBookingConfirmed(details: {
  bookingPage: string;
  eventType?: string;
}): void {
  trackEvent('demo_booking_confirmed', {
    page_path: window.location.pathname,
    booking_page: details.bookingPage,
    booking_provider: 'oncehub',
    booking_event: details.eventType,
    lead_intent: 'book_demo',
    source: 'complyhub_website',
  });

  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'qualify_lead', {
      page_path: window.location.pathname,
      booking_provider: 'oncehub',
      lead_intent: 'book_demo',
      source: 'complyhub_website',
    });
  }
}

// ============================================
// Maturity Check Quiz Analytics
// ============================================

/**
 * Track when user starts the maturity check quiz
 */
export function trackQuizStart(source?: string): void {
  trackEvent('quiz_start', {
    quiz_name: 'maturity_check',
    source: source || 'direct',
  });
  trackEvent('scorecard_started', { scorecard: 'compliance_scorecard', source: source || 'direct' });
}

/**
 * Compliance Scorecard completion. Call only after the server confirms a new
 * (non-duplicate) saved submission. Email delivery status is reported separately.
 */
export function trackScorecardCompleted(scorePercent: number, band: string, emailStatus: string): void {
  trackEvent('scorecard_completed', { scorecard: 'compliance_scorecard', score_percent: scorePercent, band });
  trackEvent('scorecard_report_delivery', { scorecard: 'compliance_scorecard', status: emailStatus });
}

/**
 * Track quiz step progression
 */
export function trackQuizStep(
  phase: 'qualifiers' | 'questions' | 'contact',
  stepNumber: number,
  totalSteps: number
): void {
  trackEvent('quiz_step', {
    quiz_name: 'maturity_check',
    phase,
    step_number: stepNumber,
    total_steps: totalSteps,
    progress_percent: Math.round((stepNumber / totalSteps) * 100),
  });
}

/**
 * Track quiz question answered
 */
export function trackQuizAnswer(
  questionId: string,
  questionType: 'qualifier' | 'scored',
  score?: number
): void {
  trackEvent('quiz_answer', {
    quiz_name: 'maturity_check',
    question_id: questionId,
    question_type: questionType,
    score,
  });
}

/**
 * Track quiz completion and results
 */
export function trackQuizComplete(
  scorePercent: number,
  band: string,
  topGapsCount: number
): void {
  trackEvent('quiz_complete', {
    quiz_name: 'maturity_check',
    score_percent: scorePercent,
    maturity_band: band,
    top_gaps_count: topGapsCount,
  });
}

/**
 * Track quiz abandonment (when user navigates away)
 */
export function trackQuizAbandon(
  phase: 'qualifiers' | 'questions' | 'contact',
  stepNumber: number,
  totalSteps: number
): void {
  trackEvent('quiz_abandon', {
    quiz_name: 'maturity_check',
    phase,
    step_number: stepNumber,
    total_steps: totalSteps,
    progress_percent: Math.round((stepNumber / totalSteps) * 100),
  });
}

/**
 * Track time spent on a quiz question
 */
export function trackQuestionTime(
  questionId: string,
  questionType: 'qualifier' | 'scored',
  timeSpentMs: number,
  stepNumber: number
): void {
  trackEvent('quiz_question_time', {
    quiz_name: 'maturity_check',
    question_id: questionId,
    question_type: questionType,
    time_spent_ms: timeSpentMs,
    time_spent_seconds: Math.round(timeSpentMs / 1000),
    step_number: stepNumber,
  });
}
